import React from 'react';

/**
 * SegmentedTabs — single shared tab component used everywhere.
 *
 * Container: warm neutral bg, subtle border, radius 8, padding 4, gap 4.
 * Item: 32px, padding 0 12, font 13/600, radius 6, color muted.
 * Count: light neutral circular badge with extra spacing from the label.
 * Active: white bg, ink color, soft shadow, no dark border.
 *
 * Props:
 *   tabs    { id, label, count? }[]
 *   active  string
 *   onChange (id) => void
 *   ariaLabel string?
 *   size    'sm' | 'md'   md = default 32px, sm = 28px
 */
export const SegmentedTabs = ({
  tabs = [],
  active,
  onChange,
  ariaLabel = 'View options',
  size = 'md',
  className = '',
}) => {
  const heightClass = size === 'sm' ? 'h-7' : 'h-8';
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={[
        'inline-flex items-center gap-1 p-1 rounded-[8px] border border-black/[0.08]',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      style={{ background: '#f3f1e9' }}
    >
      {tabs.map((tab) => {
        const isActive = tab.id === active;
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onChange?.(tab.id)}
            className={[
              heightClass,
              'inline-flex items-center px-3 rounded-[6px]',
              'text-[13px] font-semibold leading-none transition-colors',
              'border-0 outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6fb96a] focus-visible:ring-offset-1',
              isActive
                ? 'bg-white text-lb-ink-strong shadow-lb-card'
                : 'bg-transparent text-lb-muted hover:text-lb-ink-strong hover:bg-white/35',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            <span>{tab.label}</span>
            {typeof tab.count === 'number' && (
              <span
                className={[
                  'ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full',
                  'text-[11px] font-semibold leading-none',
                  isActive
                    ? 'bg-[#eef0ec] text-lb-ink-strong'
                    : 'bg-black/[0.06] text-lb-muted',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                {tab.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};
